/**
 * Generic sort of elements by a custom order of ids.
 * Elements not present in the sort order are placed at the end, preserving their
 * original relative order.
 */
const sortByIdOrder = <T extends { id: string }>(items: T[], sortOrder: string[]): T[] => {
  return [...items].sort((a, b) => {
    const indexA = sortOrder.indexOf(a.id);
    const indexB = sortOrder.indexOf(b.id);

    // Items not in sortOrder go to the end, preserving their original order
    if (indexA === -1 && indexB === -1) return 0;
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });
};

/**
 * Shape of an upsale group that may carry a per-group ordering for its items.
 * Both `items` and `itemsSortingOrder` are optional so the helper also works for
 * queries that do not (yet) select those fields.
 */
type SortableUpsale = {
  id: string;
  items?: { id: string }[];
  itemsSortingOrder?: string[] | null;
};

/**
 * Sorts upsale groups by the provided `sortOrder` (the item's `upsalesSortOrder`)
 * and, for each group, sorts its items by the group's own `itemsSortingOrder`.
 *
 * When a group has no `itemsSortingOrder` (null/undefined/empty) the item order is
 * left untouched, falling back to the order returned by the backend.
 *
 * The item-level sorting lives here so every place that sorts upsales gets
 * consistent item ordering for free.
 */
export const sortUpsales = <T extends SortableUpsale>(upsales: T[], sortOrder: string[]): T[] => {
  return sortByIdOrder(upsales, sortOrder).map((upsale) => {
    // No items to sort, or no custom order provided -> keep the group untouched.
    if (!upsale.items?.length || !upsale.itemsSortingOrder?.length) {
      return upsale;
    }

    // Sort this group's items by its group-specific order.
    return {
      ...upsale,
      items: sortByIdOrder(upsale.items, upsale.itemsSortingOrder),
    } as T;
  });
};
